/* Shared by the product gallery and existing product previews. */
(() => {
  'use strict';
  const warned = new Set();
  function select(data, variantId) {
    if (!data?.shared?.length) return null;
    const fail = reason => {
      const key = `${data.productId}:${reason}`;
      if (!warned.has(key)) { console.warn('ROCKBROS gallery: showing all media;', key); warned.add(key); }
      return null;
    };
    if (!data.complete || !Array.isArray(data.media) || !Array.isArray(data.variants)) return fail('incomplete data');
    const options = data.options || [];
    const packIndexes = options.map((name, i) => /^(verpakking|packung|pack|package|包装)$/i.test(name.trim()) ? i : -1).filter(i => i >= 0);
    const colorIndex = options.findIndex(name => /^(color|colour|couleur|farbe|kleur|kies je kleur|颜色)$/i.test(name.trim()));
    const ids = new Set(data.media.map(m => String(m.id)));
    const shared = new Set(data.shared.map(String));
    if ([...shared].some(id => !ids.has(id))) return fail('shared image absent from product media');
    const nodes = new Map(), signatures = new Map(), colours = new Map();
    for (const variant of data.variants) {
      const id = String(variant.mediaId || '');
      if (!ids.has(id) || shared.has(id) || variant.options?.length !== options.length) return fail('missing or shared variant main image');
      const colour = variant.options[colorIndex];
      if (colorIndex >= 0 && colours.has(id) && colours.get(id) !== colour) return fail('main image belongs to several colours');
      colours.set(id, colour);
      nodes.set(id, id);
    }
    const groupOf = id => { while (nodes.get(id) !== id) id = nodes.get(id); return id; };
    // Only packaging may share distinct main-image nodes. Different size nodes stay separate.
    if (packIndexes.length === 1) {
      for (const variant of data.variants) {
        const id = String(variant.mediaId);
        const signature = JSON.stringify(variant.options.filter((_, i) => i !== packIndexes[0]));
        if (signatures.has(signature)) nodes.set(groupOf(id), groupOf(signatures.get(signature)));
        else signatures.set(signature, id);
      }
    }
    const selected = data.variants.find(v => String(v.id) === String(variantId));
    if (!selected) return fail('unknown variant');
    const selectedGroup = groupOf(String(selected.mediaId));
    const result = [], seen = new Set();
    let group;
    for (const media of data.media) {
      const id = String(media.id);
      if (!shared.has(id) && nodes.has(id)) group = groupOf(id);
      if (!shared.has(id) && !nodes.has(id) && !group) return fail('detail image before any variant node');
      if (shared.has(id) || (nodes.has(id) ? id === String(selected.mediaId) : group === selectedGroup)) {
        if (!seen.has(id)) { result.push(media); seen.add(id); }
      }
    }
    return result;
  }
  const cache = new Map();
  async function load(product) {
    if (!product?.handle) return null;
    if (!cache.has(product.handle)) {
      const request = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 5000);
        try {
          const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}?view=rbnl-shared-gallery`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw Error(response.status);
          const data = await response.json();
          if (String(data.productId) !== String(product.id)) return null;
          return data;
        } catch (error) { console.warn('ROCKBROS gallery: retaining original images', error); return null; }
        finally { clearTimeout(timer); }
      })();
      cache.set(product.handle, request);
    }
    return cache.get(product.handle);
  }
  const api = { select, load };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.RbnlSharedGallery = api;
})();
